/**
 * The Reach and the score (M3 plan R11; audio-direction §4.3–§4.4):
 * - `regionMusicKey`: a galaxy region → its `music.json` region (mode, tonic palette, tempo).
 * - `systemSeedOf`: the seed that picks a system's tonic and tempo within its region's palette (the layout seed).
 * - `musicFloorOf`: the least intensity the Reach asks for: Climax during a relay reboot, Tension during an
 *   interdiction, otherwise Calm (the combat feed still raises it). Pure and allocation-free.
 */
import { logger } from '../../core/logger'
import type { RegionId } from '../../galaxy/types'
import type { ReachCore } from '../../reach/runtime/types'
import { LEVEL, type Level } from '../music/intensity'

/** Galaxy region → music.json region. */
export const REGION_MUSIC: Readonly<Record<RegionId, string>> = Object.freeze({
  gate: 'gateCluster',
  mid: 'midreach',
  deep: 'deepReach',
  rim: 'rim',
})

export function regionMusicKey(region: RegionId): string {
  return REGION_MUSIC[region]
}

/**
 * The seed of system `index` (its layout seed): from the loaded layout when it is that system, else from the
 * runtime's layout cache. A runtime without layouts (R1's stub) falls back to a hash of the galaxy seed and index.
 * Rare events only (a transit, a system entry): may allocate.
 */
export function systemSeedOf(core: Pick<ReachCore, 'galaxy' | 'session' | 'layoutOf'>, index: number): number {
  const layout = core.session.layout
  if (layout !== null && layout.systemIndex === index) return layout.seed
  const system = core.galaxy.systems[index]
  if (system === undefined) return fallbackSeed(core.galaxy.seed, index)
  try {
    return core.layoutOf(system.id).seed
  } catch (err: unknown) {
    logger.warn(`reach audio: no layout for ${system.id}; the score picks its key from a derived seed`, err)
    return fallbackSeed(core.galaxy.seed, index)
  }
}

function fallbackSeed(galaxySeed: number, index: number): number {
  const h = Math.imul(galaxySeed ^ 0x2545f491, 0x9e3779b1) ^ Math.imul(index + 1, 0x85ebca77)
  return (h ^ (h >>> 15)) >>> 0
}

/** Climax during a relay reboot, Tension during an interdiction, otherwise Calm. */
export function musicFloorOf(core: Pick<ReachCore, 'travel' | 'relink'>): Level {
  if (core.relink.phase !== 'idle') return LEVEL.climax
  if (core.travel.interdiction.active || core.travel.mode === 'interdicted') return LEVEL.tension
  return LEVEL.calm
}
